import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  Renderer2,
  SecurityContext,
  effect,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { PopupService } from 'src/app/core/services/popup/popup.service';
import { ContactComponent } from '../contact/contact.component';
import { ChatbotDisplayType, ChatbotMessage } from '../../types/chatbot.model';

export const CHATBOT_MESSAGE_LIMIT = 20;
import { environment } from 'src/environments/environment';
import { ChatbotService } from './chatbot.service';
import { marked } from 'marked';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class ChatbotComponent implements OnInit {
  chatbot_url = environment.chatbot_url;
  // Dependency injections
  private readonly renderer = inject(Renderer2);

  // Input properties
  readonly displayFormat = input<ChatbotDisplayType>('collapsible');
  // isCollapsibleChatbotOpen = input<boolean>(true);
  isCollapsibleChatbotOpen = model<boolean>(true);

  // ViewChild references
  private readonly messageContainer = viewChild<ElementRef>('messageContainer');

  // Form controls
  protected message = new FormControl('', { nonNullable: true });

  // Signal-based state
  readonly messages = signal<ChatbotMessage[]>([]);
  readonly canSendMessage = signal<boolean>(true);
  readonly preparedPrompts = signal<string[]>([]);
  readonly userMessageCount = signal<number>(0);

  get remainingMessages(): number {
    return CHATBOT_MESSAGE_LIMIT - this.userMessageCount();
  }

  get limitReached(): boolean {
    return this.userMessageCount() >= CHATBOT_MESSAGE_LIMIT;
  }

  constructor(private chatbotService: ChatbotService, private sanitizer: DomSanitizer,
              private translate: TranslateService, private popupService: PopupService) {
    effect(() => {
      if(this.messages().length > 0) {
        setTimeout(() => this.scrollToBottom(), 0);
      }
    });
  }

  /**
   * Handles click events on dynamic content (like the contact link)
   */
  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    const target = event.target as HTMLElement;
    if(target.classList.contains('contact-link')) {
      event.preventDefault();
      this.openContactDialog();
    }
  }

  /**
   * Opens the contact dialog without changing the URL
   */
  private openContactDialog(): void {
    this.popupService.openDialog(ContactComponent);
  }

  /**
   * Initialize the component with a welcome message
   */
  ngOnInit(): void {
    //   const renderer = new marked.Renderer();

    // renderer.paragraph = (token: Token) => `<span class="markdown-p">${token.raw}</span>`;
    // renderer.strong = (token: Token) => `<strong class="markdown-strong">${token.raw}</strong>`;
    // renderer.em = (token: Token) => `<em class="markdown-em">${token.raw}</em>`;
    // renderer.code = (token: Token) => `<code class="markdown-code">${token.raw}</code>`;
    // renderer.list = (body: string, ordered: boolean) => {
    //   const tag = ordered ? 'ol' : 'ul';
    //   return `<${tag} class="markdown-list">${body}</${tag}>`;
    // };
    // renderer.listitem = (text: string) => `<li class="markdown-li">${text}</li>`;

    // marked.setOptions({ renderer });

    const welcomeMessage =
      this.translate.currentLang === 'fr'
        ? "Bonjour ! Comment puis-je vous aider aujourd'hui ?"
        : 'Hello! How can I assist you today?';
    this.messages.set([
      { type: 'bot', text: 'CHATBOT_WELCOME_MESSAGE', timestamp: new Date() },
    ]);
    this.setPreparedPrompts();
    this.translate.onLangChange.subscribe(() => {
      this.setPreparedPrompts();
    });
  }

  private setPreparedPrompts(): void {
    this.translate
      .get([
        'CHATBOT_PROMPT_1',
        'CHATBOT_PROMPT_2',
        'CHATBOT_PROMPT_3',
        'CHATBOT_PROMPT_4',
      ])
      .subscribe((translations) => {
        this.preparedPrompts.set([
          translations['CHATBOT_PROMPT_1'],
          translations['CHATBOT_PROMPT_2'],
          translations['CHATBOT_PROMPT_3'],
          translations['CHATBOT_PROMPT_4'],
        ]);
      });
  }

  /**
   * Sends the user's message and processes the response
   */
  protected sendUserMessage(): void {
    const messageText = this.message?.value?.trim();
    if(!messageText || !this.canSendMessage() || this.limitReached) {
      return;
    }
    // Add user message to the chat
    this.messages.update((messages) => [
      ...messages,
      { type: 'user', text: messageText, timestamp: new Date() },
    ]);
    this.userMessageCount.update((count) => count + 1);

    // Get bot response with the captured text
    this.getAutoBotMessage(messageText);
    this.message.reset();
  }

  protected sendPreparedPrompt(prompt: string): void {
    this.message.setValue(prompt);
    this.sendUserMessage();
  }

  /**
   * Handles enter key press in the input field
   * @param event The keyboard event
   */
  protected onEnterPressed(event: Event): void {
    event.preventDefault();
    this.sendUserMessage();
  }

  /**
   * Generates a bot message response
   * @param initMessage Optional custom message text
   */
  private getAutoBotMessage(message: string): void {
    this.canSendMessage.set(false);

    // Show loading indicator
    this.messages.update((messages) => [...messages, { type: 'loading' }]);

    this.chatbotService
      .ask(message)
      .then((response) => {
        this.messages.update((messages) => [
          ...messages.slice(0, -1),
          { type: 'bot', text: '', timestamp: new Date(), messageId: response.messageId },
        ]);
        if(!environment.production) {
          console.log(response.answer);
        }

        this.streamBotMessage(response.answer);
      })
      .catch((error) => {
        this.messages.update((messages) => [
          ...messages.slice(0, -1),
          {
            type: 'bot',
            text: this.translate.instant('CHATBOT_ERROR_GENERIC'),
            timestamp: new Date(),
          },
        ]);
        if(!environment.production) {
          console.error('Error occurred while fetching chatbot response:', error);
        }
        this.canSendMessage.set(true);
      });
  }

  /**
   * Streams bot message character by character for a typing effect
   * Handles background tabs by catching up when the user returns
   * @param fullMessage The complete message to stream
   */
  private streamBotMessage(fullMessage: string): void {
    const streamDelay = 10; // Milliseconds between each character
    const startTime = performance.now();

    const streamInterval = setInterval(() => {
      const elapsed = performance.now() - startTime;
      const expectedIndex = Math.floor(elapsed / streamDelay);
      const targetIndex = Math.min(expectedIndex, fullMessage.length);

      if(targetIndex > 0) {
        const textToShow = fullMessage.substring(0, targetIndex);
        this.messages.update((messages) => {
          const lastMessage = messages[messages.length - 1];
          return [
            ...messages.slice(0, -1),
            { ...lastMessage, text: textToShow },
          ];
        });
      }

      if(targetIndex >= fullMessage.length) {
        clearInterval(streamInterval);
        this.canSendMessage.set(true);
        this.onBotResponseComplete();
      }
    }, streamDelay);
  }

  private onBotResponseComplete(): void {
    if(this.limitReached) {
      this.canSendMessage.set(false);
      const limitMessage = this.translate.instant('CHATBOT_LIMIT_REACHED');
      this.messages.update((messages) => [
        ...messages,
        { type: 'bot', text: limitMessage, timestamp: new Date() },
      ]);
    }
  }

  /**
   * Formats message text, converting markdown to HTML if needed
   * @param text The message text
   * @param isBotMessage Whether the message is from the bot
   */
  protected formatMessage(text: string, isBotMessage: boolean): string {
    if(isBotMessage && text === 'CHATBOT_WELCOME_MESSAGE') {
      return this.translate.instant('CHATBOT_WELCOME_MESSAGE');
    }
    if(isBotMessage) {
      const email = '`webmaster.infotech@renault.com`';
      const emailPlain = 'webmaster.infotech@renault.com';
      const clickHereTextFr = 'Cliquez ici';
      const clickHereTextEn = 'click here';
      const clickHereTextBis = 'click [here](mailto:webmaster.infotech@renault.com)';

      const supportText =
        this.translate.currentLang === 'fr' ? 'Cliquez ici' : 'click here';
      const supportLink = `<span class="contact-link font-bold text-blue-600 underline cursor-pointer">${supportText}</span>`;

      if(text.toLowerCase().includes(clickHereTextFr.toLowerCase()) || text.toLowerCase().includes(clickHereTextEn.toLowerCase()) || text.toLowerCase().includes(clickHereTextBis)) {
        text = text.replace(new RegExp(clickHereTextFr, 'gi'), supportLink);
        text = text.replace(new RegExp(clickHereTextEn, 'gi'), supportLink);
        text = text.replace(new RegExp(clickHereTextBis, 'gi'), supportLink);
        text = text.replace(new RegExp('\n', 'gi'), "  <br>");
      }

      // Convert markdown to HTML for bot messages
      return this.convertMarkdownToHtml(text);
    }
    return text;
  }

  convertMarkdownToHtml(markdown: string): string {
    const renderer = new marked.Renderer();

    // Override link rendering to add target="_blank"
    renderer.link = ({ href, title, text }) => {
      const titleAttr = title ? ` title="${title}"` : '';
      return `<a href="${href}"${titleAttr} class="font-bold text-blue-600 underline cursor-pointer" target="_blank" rel="noopener noreferrer">${text}</a>`;
    };

    markdown = marked(markdown, { renderer, breaks: true, gfm: true }).toString();

    return this.sanitizer.sanitize(SecurityContext.HTML, markdown) || '';
  }

  /**
   * Formats timestamp for display
   * @param timestamp The message timestamp
   */
  protected formatTime(timestamp?: Date): string {
    if(!timestamp) return '';

    const now = new Date();
    const messageDate = new Date(timestamp);
    const diffMs = now.getTime() - messageDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    // Less than 1 minute ago
    if(diffMins < 1) return 'Just now';

    // Less than 60 minutes ago
    if(diffMins < 60) return `${diffMins}m ago`;

    // Less than 24 hours ago
    const diffHours = Math.floor(diffMins / 60);
    if(diffHours < 24) return `${diffHours}h ago`;

    // Otherwise show time
    return messageDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Scrolls the message container to the bottom
   */
  private scrollToBottom(): void {
    if(this.messageContainer()?.nativeElement) {
      this.renderer.setProperty(
        this.messageContainer()?.nativeElement,
        'scrollTop',
        this.messageContainer()?.nativeElement.scrollHeight,
      );
    }
  }

  /**
   * Marks the clicked feedback button as active, then fades out and removes the feedback block
   * @param message The bot message being rated
   * @param feedback Whether the user liked or disliked the message
   */
  protected onFeedbackClick(message: ChatbotMessage, feedback: 'like' | 'dislike'): void {
    if(message.feedback) return;

    if(message.messageId) {
      this.chatbotService.sendFeedback(message.messageId, feedback);
    }

    // Match on timestamp since the message object reference changes on each update
    const isTargetMessage = (m: ChatbotMessage): boolean => m.timestamp === message.timestamp;

    this.messages.update((messages) =>
      messages.map((m) => (isTargetMessage(m) ? { ...m, feedback } : m)),
    );

    setTimeout(() => {
      this.messages.update((messages) =>
        messages.map((m) => (isTargetMessage(m) ? { ...m, feedbackHiding: true } : m)),
      );
    }, 700);

    setTimeout(() => {
      this.messages.update((messages) =>
        messages.map((m) => (isTargetMessage(m) ? { ...m, feedbackHidden: true } : m)),
      );
    }, 1000);
  }
}
